import type { AdminTicketDetailsDto, DicoStatusDto } from "@/api/types";
import dicoPng from "@/assets/images/integrations/dico.webp";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { BrandMark } from "@/design-system/BrandMark";
import { Button } from "@/design-system/Button";
import { Card, CardCenteredState } from "@/design-system/Card";
import { Inline } from "@/design-system/Inline";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { Text } from "@/design-system/Text";
import { ticketMutations } from "@/queries/tickets/mutations";
import { useTranslation } from "@/translations";

export function TicketDicoCard({
  ticket,
  dico,
}: {
  ticket: AdminTicketDetailsDto;
  dico: DicoStatusDto;
}): React.ReactNode {
  const { t } = useTranslation();
  const uploadTicketMutation = ticketMutations.useSendTicketToDico(ticket.id);
  const canSendDicoTicket = ticket.address != null;

  return (
    <Card>
      <Inline justify="center">
        <BrandMark src={dicoPng} alt="Dico logo" />
      </Inline>
      {uploadTicketMutation.isPending ? (
        <CardCenteredState>
          <Spinner size="sm" />
        </CardCenteredState>
      ) : (
        <Stack gap="md" align="center">
          {dico.state === "notStarted" && (
            <Text variant="body-spacious" tone="secondary" overflow="normal" data-testid="ticket-integration-status">
              {t("page.tickets.dico.state")} {t("page.tickets.dico.state.not-uploaded")}
            </Text>
          )}
          {dico.state === "failed" && (
            <Text variant="body-spacious" tone="secondary" overflow="normal" data-testid="ticket-integration-status">
              {t("page.tickets.dico.state")}{" "}
              <Text variant="body-spacious" tone="danger-secondary" overflow="normal">
                {dico.errorDetails || t("page.tickets.dico.state.error")}
              </Text>
            </Text>
          )}
          {dico.state === "sent" && (
            <Text
              as="div"
              variant="body-emphasis"
              tone="success"
              align="center"
              data-testid="ticket-integration-status"
            >
              <Inline gap="xs" align="center" wrap={false}>
                <CheckIcon size={16} />
                {t("page.tickets.dico.state.success")}
              </Inline>
            </Text>
          )}

          {dico.state !== "sent" && (
            <Inline>
              <Button
                onClick={() => uploadTicketMutation.mutate()}
                disabled={!canSendDicoTicket}
                fullWidth
                title={canSendDicoTicket ? undefined : t("page.tickets.dico.actions.send-ticket-disabled")}
              >
                {t("page.tickets.dico.actions.send-ticket")}
              </Button>
            </Inline>
          )}
        </Stack>
      )}
    </Card>
  );
}
